import { Draggable, BaseEvent } from '@shopify/draggable';
import { SortableStartEvent } from '@shopify/draggable/build/esnext/Sortable/SortableEvent/SortableEvent.mjs';
class SortableStopEvent extends BaseEvent {
  static type = 'sortable:stop';

  /**
   * Original index on sortable start
   * @property oldIndex
   * @type {Number}
   * @readonly
   */
  get source() {
    return this.data.source;
  }

  /**
   * New index of draggable element
   * @property newIndex
   * @type {Number}
   * @readonly
   */
  get over() {
    return this.data.over;
  }

  /**
   * Original index on sortable start
   * @property oldIndex
   * @type {Number}
   * @readonly
   */
  get sourceGroupId() {
    return this.data.sourceGroupId;
  }

  /**
   * New index of draggable element
   * @property newIndex
   * @type {Number}
   * @readonly
   */
  get overGroupId() {
    return this.data.overGroupId;
  }

  /**
   * New index of draggable element
   * @property newIndex
   * @type {Number}
   * @readonly
   */
  get position() {
    return this.data.position;
  }
}

const onDragStart = Symbol('onDragStart');
const onDragMove = Symbol('onDragMove');
const onDragStop = Symbol('onDragStop');

// requestanimation with settimeout fallback
function raf(cb) {
  (
    window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    function (callback) {
      window.setTimeout(callback, 1000 / 60);
    }
  )(cb);
}

const matchFunction =
  Element.prototype.matches ||
  Element.prototype.webkitMatchesSelector ||
  Element.prototype.mozMatchesSelector ||
  Element.prototype.msMatchesSelector;

/**
 * Get the closest parent element of a given element that matches the given
 * selector string or matching function
 *
 * @param {Element} element The child element to find a parent of
 * @param {String|Function} selector The string or function to use to match
 *     the parent element
 * @return {Element|null}
 */
function closest(element, condition) {
  if (!element) {
    return null;
  }

  const normalizedConditions = Array.isArray(condition) ? condition : [condition];

  const isMatch = (el, condition) => {
    const selector = condition;
    const callback = condition;
    const nodeList = condition;
    const singleElement = condition;

    const isSelector = Boolean(typeof condition === 'string');
    const isFunction = Boolean(typeof condition === 'function');
    const isNodeList = Boolean(condition instanceof NodeList || condition instanceof Array);
    const isElement = Boolean(condition instanceof HTMLElement);

    if (!el) {
      return el;
    } else if (isSelector) {
      return matchFunction.call(el, selector);
    } else if (isNodeList) {
      return [...nodeList].includes(el);
    } else if (isElement) {
      return singleElement === el;
    } else if (isFunction) {
      return callback(el);
    } else {
      return null;
    }
  };

  let current = element;

  do {
    current = current.correspondingUseElement || current.correspondingElement || current;

    if (normalizedConditions.some((perCondition) => isMatch(current, perCondition))) {
      return current;
    }

    current = current.parentNode;
  } while (current && current !== document.body && current !== document);

  return null;
}

/**
 * Returns announcement message when a Draggable element has been sorted with another Draggable element
 * or moved into a new container
 * @param {SortableSortedEvent} sortableEvent
 * @return {String}
 */
function onSortableSortedDefaultAnnouncement({ dragEvent }) {
  const sourceText = dragEvent.source.textContent.trim() || dragEvent.source.id || 'sortable element';

  if (dragEvent.over) {
    const overText = dragEvent.over.textContent.trim() || dragEvent.over.id || 'sortable element';
    const isFollowing = dragEvent.source.compareDocumentPosition(dragEvent.over) & Node.DOCUMENT_POSITION_FOLLOWING;

    if (isFollowing) {
      return `Placed ${sourceText} after ${overText}`;
    } else {
      return `Placed ${sourceText} before ${overText}`;
    }
  } else {
    // need to figure out how to compute container name
    return `Placed ${sourceText} into a different container`;
  }
}

/**
 * @const {Object} defaultAnnouncements
 * @const {Function} defaultAnnouncements['sortable:sorted']
 */
const defaultAnnouncements = {
  'sortable:sorted': onSortableSortedDefaultAnnouncement
};

/**
 * Sortable is built on top of Draggable and allows sorting of draggable elements. Sortable will keep
 * track of the original index and emits the new index as you drag over draggable elements.
 * @class Sortable
 * @module Sortable
 * @extends Draggable
 */
export default class Sortable extends Draggable {
  /**
   * Sortable constructor.
   * @constructs Sortable
   * @param {HTMLElement[]|NodeList|HTMLElement} containers - Sortable containers
   * @param {Object} options - Options for Sortable
   */
  constructor(containers = [], options = {}) {
    super(containers, {
      ...options,
      announcements: {
        ...defaultAnnouncements,
        ...(options.announcements || {})
      }
    });

    this[onDragStart] = this[onDragStart].bind(this);
    this[onDragMove] = this[onDragMove].bind(this);
    this[onDragStop] = this[onDragStop].bind(this);

    this.on('drag:start', this[onDragStart]).on('drag:move', this[onDragMove]).on('drag:stop', this[onDragStop]);

    this.lastOver = null;
    this.lastOverInfo = {};
    this.placeholderWidth = 0;
    this.draggablePlaceholder = null;
  }

  /**
   * Destroys Sortable instance.
   */
  destroy() {
    super.destroy();

    this.off('drag:start', this[onDragStart]).off('drag:stop', this[onDragStop]);

    this.draggablePlaceholder?.remove();
    this.draggablePlaceholder = null;
    this.lastOver = null;
  }

  /**
   * Returns sortable elements for a given container, excluding the mirror and
   * original source element if present
   * @param {HTMLElement} container
   * @return {HTMLElement[]}
   */
  getSortableElementsForContainer(container) {
    const allSortableElements = container.querySelectorAll(this.options.draggable);

    return [...allSortableElements].filter((childElement) => {
      return childElement !== this.originalSource && childElement !== this.mirror && childElement.parentNode === container;
    });
  }

  /**
   * Drag start handler
   * @private
   * @param {DragStartEvent} event - Drag start event
   */
  [onDragStart](event) {
    this.placeholderWidth = event.source.offsetWidth;

    const sortableStartEvent = new SortableStartEvent({
      dragEvent: event
    });

    this.trigger(sortableStartEvent);

    this.draggablePlaceholder = document.createElement('div');
    this.draggablePlaceholder.classList.add('draggable-placeholder');
    this.draggablePlaceholder.style.display = 'none';
    document.body.appendChild(this.draggablePlaceholder);

    if (sortableStartEvent.canceled()) {
      event.cancel();
    }
  }

  /**
   * Drag over handler
   * @private
   * @param {DragOverEvent} event - Drag over event
   */
  [onDragMove](event) {
    const { source, sensorEvent, originalEvent } = event;
    const over = closest(sensorEvent.target, [this.options.draggable, '.erercise-sub-group']);
    // const over = closest(sensorEvent.target, [this.options.draggable, '.erercise-sub-group']) || sensorEvent.target;
    if (over === event.originalSource || over === event.source) {
      return;
    }

    const parseOverInfo = (overEl, sourceEl) => {
      if (overEl) {
        const overExerciseId = overEl.dataset.exerciseId;
        const groupable = overEl.parentElement === this.containers[0] && overExerciseId;
        const isOverInGroup = overEl.parentElement !== this.containers[0];
        const clientRect4over = overEl.getBoundingClientRect();
        const overIsGroup = overEl.classList.contains('erercise-sub-group');

        if (overIsGroup && originalEvent.clientY > clientRect4over.top && originalEvent.clientY < clientRect4over.bottom) {
          return;
        }

        const area = [20, clientRect4over.height - 20];
        const placeholderInfo = {
          height: 8,
          position: '',
          left: clientRect4over.left,
          top: originalEvent.clientY,
          width: overEl.offsetWidth
        };
        // 判断指示块出现的位置
        if (originalEvent.clientY < clientRect4over.top + area[0]) {
          // 顶部
          placeholderInfo.position = 'top';
          placeholderInfo.top = clientRect4over.top - 4 - 8;
        } else if (
          groupable &&
          clientRect4over.top + area[0] <= originalEvent.clientY &&
          originalEvent.clientY < clientRect4over.top + area[1]
        ) {
          // 中部
          placeholderInfo.position = 'center';
          placeholderInfo.top = clientRect4over.top + area[0];
          placeholderInfo.height = area[1] - area[0];
        } else {
          // 底部
          placeholderInfo.position = 'bottom';
          placeholderInfo.top = clientRect4over.bottom + 4;
        }

        return {
          over: overExerciseId,
          source: sourceEl.dataset.exerciseId,
          overGroupId: overEl.dataset.groupId,
          sourceGroupId: sourceEl.dataset.groupId,
          isOverInGroup,
          placeholderInfo
        };
      }
    };

    this.lastOver = this.lastOver || over;
    const overInfo = parseOverInfo(over || this.lastOver, source);
    if (overInfo) {
      this.lastOverInfo = overInfo;
      const placeholderInfo = { ...overInfo.placeholderInfo };
      raf(() => {
        Object.assign(this.draggablePlaceholder.style, {
          position: 'fixed',
          top: `${placeholderInfo.top}px`,
          left: `${placeholderInfo.left}px`,
          height: `${placeholderInfo.height}px`,
          width: `${placeholderInfo.width}px`,
          background: '#fff',
          pointerEvents: 'none',
          zIndex: 10000,
          display: null
        });
      });
    }
  }

  /**
   * Drag stop handler
   * @private
   * @param {DragStopEvent} event - Drag stop event
   */
  [onDragStop](event) {
    this.draggablePlaceholder.style.display = 'none';
    this.lastOver = null;
    const sortableStopEvent = new SortableStopEvent({
      dragEvent: event,
      source: this.lastOverInfo.source,
      over: this.lastOverInfo.over,
      sourceGroupId: this.lastOverInfo.sourceGroupId,
      overGroupId: this.lastOverInfo.overGroupId,
      position: this.lastOverInfo.placeholderInfo.position
    });

    this.trigger(sortableStopEvent);
  }
}
